import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@headlessui/react';
import { ShieldCheckIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { PERMISSION_KEYS, ROLE_DEFAULT_PERMISSIONS } from '@wooconnect/shared';
import type { PermissionKey } from '@wooconnect/shared';
import { useRoleDefaults, useUpdateRoleDefaults } from '@/hooks/useAdmin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/utils/cn';

const EDITABLE_ROLES = ['store_owner', 'manager', 'staff'] as const;
type EditableRole = (typeof EDITABLE_ROLES)[number];
type RolePermissions = Record<EditableRole, Record<PermissionKey, boolean>>;

const PERMISSION_KEY_TO_CAMEL: Record<PermissionKey, string> = {
  orders_view: 'ordersView',
  orders_edit: 'ordersEdit',
  orders_refund: 'ordersRefund',
  products_view: 'productsView',
  products_edit: 'productsEdit',
  customers_view: 'customersView',
  analytics_view: 'analyticsView',
  analytics_advanced: 'analyticsAdvanced',
  notifications_enabled: 'notificationsEnabled',
  settings_view: 'settingsView',
  settings_edit: 'settingsEdit',
  ai_chat: 'aiChat',
};

export function AdminRoleDefaultsPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useRoleDefaults();
  const updateMutation = useUpdateRoleDefaults();

  const [local, setLocal] = useState<RolePermissions | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (data) {
      setLocal(structuredClone(data) as RolePermissions);
      setHasChanges(false);
    }
  }, [data]);

  const toggle = useCallback((role: EditableRole, key: PermissionKey) => {
    setLocal((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      next[role][key] = !next[role][key];
      return next;
    });
    setHasChanges(true);
  }, []);

  const handleSave = () => {
    if (!local) return;
    updateMutation.mutate(local);
  };

  const handleReset = () => {
    if (!confirm(t('admin.roleDefaultsResetConfirm'))) return;
    const defaults: RolePermissions = {
      store_owner: { ...ROLE_DEFAULT_PERMISSIONS.store_owner },
      manager: { ...ROLE_DEFAULT_PERMISSIONS.manager },
      staff: { ...ROLE_DEFAULT_PERMISSIONS.staff },
    };
    setLocal(defaults);
    setHasChanges(true);
  };

  const handleCancel = () => {
    if (data) {
      setLocal(structuredClone(data) as RolePermissions);
      setHasChanges(false);
    }
  };

  if (isError) return <ErrorState onRetry={() => refetch()} />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheckIcon className="h-7 w-7 text-brand-600 dark:text-brand-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.roleDefaultsTitle')}</h1>
        </div>
        <Button variant="ghost" size="sm" leftIcon={<ArrowPathIcon className="h-4 w-4" />} onClick={handleReset} disabled={isLoading}>
          {t('admin.roleDefaultsReset')}
        </Button>
      </div>

      <div className="rounded-lg border border-brand-200 bg-brand-50 p-4 text-sm text-brand-800 dark:border-brand-800 dark:bg-brand-900/20 dark:text-brand-300">
        {t('admin.roleDefaultsDescription')}
      </div>

      <Card padding="sm">
        {isLoading || !local ? (
          <div className="space-y-3 p-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded" />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('admin.permissions')}
                  </th>
                  {EDITABLE_ROLES.map((role) => (
                    <th key={role} className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t(`roles.${role}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_KEYS.map((key, idx) => (
                  <tr
                    key={key}
                    className={cn(
                      'border-b border-gray-100 dark:border-gray-700/50',
                      idx % 2 === 0 && 'bg-gray-50/50 dark:bg-gray-800/30',
                    )}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t(`permissions.${PERMISSION_KEY_TO_CAMEL[key]}`)}
                    </td>
                    {EDITABLE_ROLES.map((role) => (
                      <td key={role} className="px-4 py-3 text-center">
                        <Switch
                          checked={local[role][key]}
                          onChange={() => toggle(role, key)}
                          className={cn(
                            'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
                            local[role][key] ? 'bg-brand-600 dark:bg-brand-500' : 'bg-gray-200 dark:bg-gray-600',
                          )}
                        >
                          <span
                            className={cn(
                              'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                              local[role][key] ? 'translate-x-4' : 'translate-x-0',
                            )}
                          />
                        </Switch>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {hasChanges && (
        <div className="sticky bottom-4 flex items-center justify-end gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <Button variant="ghost" onClick={handleCancel}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} loading={updateMutation.isPending}>
            {t('common.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
